import React, { createContext, useContext, useCallback, ReactNode } from 'react';
import { useErrorStore, AppError } from '../stores/useErrorStore';

interface ErrorContextType {
  // Error State
  errors: AppError[];
  activeErrors: AppError[];
  errorStats: ReturnType<typeof useErrorStore.getState>['errorStats'];
  
  // Error Actions
  addError: (error: Omit<AppError, 'id' | 'timestamp' | 'resolved' | 'retryCount'>) => void;
  removeError: (id: string) => void;
  resolveError: (id: string) => void;
  retryError: (id: string) => void;
  clearErrors: () => void;
  clearErrorHistory: () => void;
  
  // Error Handlers
  handleError: (error: Error, source: string, userAction?: string) => void;
  handleNetworkError: (error: any, source: string) => void;
  handleValidationError: (error: any, source: string) => void;
  handleAuthError: (error: any, source: string) => void;
  
  // Utility Functions
  hasErrors: () => boolean;
  hasCriticalErrors: () => boolean;
  getErrorsByType: (type: AppError['type']) => AppError[];
  getErrorsBySeverity: (severity: AppError['severity']) => AppError[];
  getErrorsBySource: (source: string) => AppError[];
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

interface ErrorProviderProps {
  children: ReactNode;
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({ children }) => {
  const {
    errors,
    activeErrors,
    errorStats,
    addError,
    removeError,
    resolveError,
    retryError,
    clearErrors,
    clearErrorHistory,
    handleError,
    handleNetworkError,
    handleValidationError,
    handleAuthError,
  } = useErrorStore();

  // Utility Functions
  const hasErrors = useCallback(() => {
    return activeErrors.length > 0;
  }, [activeErrors]);

  const hasCriticalErrors = useCallback(() => {
    return activeErrors.some(error => error.severity === 'critical');
  }, [activeErrors]);

  const getErrorsByType = useCallback((type: AppError['type']) => {
    return activeErrors.filter(error => error.type === type);
  }, [activeErrors]);

  const getErrorsBySeverity = useCallback((severity: AppError['severity']) => {
    return activeErrors.filter(error => error.severity === severity);
  }, [activeErrors]);

  const getErrorsBySource = useCallback((source: string) => {
    return activeErrors.filter(error => error.source === source);
  }, [activeErrors]);

  const contextValue: ErrorContextType = {
    errors,
    activeErrors,
    errorStats,
    addError,
    removeError,
    resolveError,
    retryError,
    clearErrors,
    clearErrorHistory,
    handleError,
    handleNetworkError,
    handleValidationError,
    handleAuthError,
    hasErrors,
    hasCriticalErrors,
    getErrorsByType,
    getErrorsBySeverity,
    getErrorsBySource,
  };

  return (
    <ErrorContext.Provider value={contextValue}>
      {children}
    </ErrorContext.Provider>
  );
};

// Custom Hook
export const useError = (): ErrorContextType => {
  const context = useContext(ErrorContext);
  if (context === undefined) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};

// Error Boundary Hook
export const useErrorBoundary = () => {
  const { handleError } = useError();
  
  const captureError = useCallback((error: Error, errorInfo?: { componentStack?: string }) => {
    handleError(error, 'ErrorBoundary', errorInfo?.componentStack);
  }, [handleError]);
  
  return { captureError };
};

export default ErrorContext;
